<template>
  <main class="index-page log-page">
    <header class="page-header">
      <h1>请求日志</h1>
      <p>查看每次转发的渠道命中、模型映射、响应时间、计费明细和错误信息。</p>
    </header>

    <section class="log-section">
      <div class="log-section-header">
        <h2>日志筛选</h2>
        <el-tag effect="plain">共 {{ total }} 条</el-tag>
      </div>

      <el-form class="filter-form" label-position="top" @submit.prevent>
        <el-form-item class="filter-date" label="开始日期">
          <el-date-picker v-model="filters.start_date" type="date" value-format="YYYY-MM-DD" placeholder="开始日期" />
        </el-form-item>
        <el-form-item class="filter-date" label="结束日期">
          <el-date-picker v-model="filters.end_date" type="date" value-format="YYYY-MM-DD" placeholder="结束日期" />
        </el-form-item>
        <el-form-item class="filter-text" label="渠道">
          <el-input v-model.trim="filters.channel" placeholder="渠道名称" />
        </el-form-item>
        <el-form-item class="filter-text" label="上游模型">
          <el-input v-model.trim="filters.model" placeholder="上游模型名称" />
        </el-form-item>
        <el-form-item class="filter-status" label="状态">
          <el-select v-model="filters.status" placeholder="状态">
            <el-option label="全部" value="all" />
            <el-option label="正常" value="normal" />
            <el-option label="异常" value="error" />
          </el-select>
        </el-form-item>
        <el-form-item class="filter-actions">
          <el-button type="primary" :loading="loading" @click="searchLogs">查询</el-button>
          <el-button :disabled="loading" @click="resetFilters">重置</el-button>
          <el-button type="danger" plain :disabled="loading || !selectedIds.length" @click="removeSelected">
            删除已选
          </el-button>
          <el-button type="danger" :disabled="loading || !logs.length" @click="clearAll">清空日志</el-button>
        </el-form-item>
      </el-form>

      <el-alert v-if="notice" class="notice-alert" :title="notice" type="success" show-icon :closable="false" />

      <div class="table-scroll">
        <el-table
          class="log-table"
          :data="logs"
          v-loading="loading"
          empty-text="暂无日志"
          stripe
          @selection-change="selectedIds = $event.map((item) => item.id)"
        >
          <el-table-column type="selection" width="48" />
          <el-table-column label="请求时间" width="155">
            <template #default="{ row }">{{ formatTime(row.created_at) }}</template>
          </el-table-column>
          <el-table-column label="本地 Key" width="135">
            <template #default="{ row }">
              <el-tooltip :content="row.local_key_full || row.local_key || '-'" placement="top" :show-after="250">
                <el-link type="primary" class="mono key-link" @click="copyKey(row)">
                  {{ row.local_key || '-' }}
                </el-link>
              </el-tooltip>
            </template>
          </el-table-column>
          <el-table-column label="命中渠道" width="150">
            <template #default="{ row }">{{ row.channel_name || channelFallback(row.channel_id) }}</template>
          </el-table-column>
          <el-table-column label="模型" width="170">
            <template #default="{ row }"><span class="mono">{{ row.model || '-' }}</span></template>
          </el-table-column>
          <el-table-column label="上游模型" width="190">
            <template #default="{ row }"><span class="mono">{{ row.upstream_model || '-' }}</span></template>
          </el-table-column>
          <el-table-column label="响应时间" width="110">
            <template #default="{ row }">
              <el-tag type="info" effect="plain">{{ formatLatency(row.latency_ms) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="状态" width="90">
            <template #default="{ row }">
              <el-tag :type="row.status_code >= 400 ? 'danger' : 'success'" effect="dark">
                {{ row.status_code }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="计费详情" width="190">
            <template #default="{ row }">
              <div class="billing-box">
                <el-tag type="success" effect="plain">扣费 {{ row.cost }}</el-tag>
                <div class="billing-grid">
                  <span>输入</span><strong>{{ row.prompt_tokens || 0 }}</strong>
                  <span>输出</span><strong>{{ row.completion_tokens || 0 }}</strong>
                  <span>总量</span><strong>{{ row.total_tokens || 0 }}</strong>
                  <span>缓存</span><strong>{{ row.cached_tokens || 0 }}</strong>
                  <span>图片</span><strong>{{ row.image_tokens || 0 }}</strong>
                  <span>输入倍率</span><strong>{{ formatRatio(row.prompt_ratio) }}</strong>
                  <span>输出倍率</span><strong>{{ formatRatio(row.completion_ratio) }}</strong>
                  <span>缓存倍率</span><strong>{{ formatRatio(row.cache_ratio) }}</strong>
                  <span>图片倍率</span><strong>{{ formatRatio(row.image_ratio) }}</strong>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="错误信息" width="220">
            <template #default="{ row }">{{ row.error_message || '-' }}</template>
          </el-table-column>
          <el-table-column label="操作" width="90" fixed="right">
            <template #default="{ row }">
              <el-button size="small" type="primary" plain @click="openDetail(row)">详情</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="pagination-bar">
        <el-pagination
          v-model:current-page="page"
          v-model:page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          background
          @size-change="handlePageSizeChange"
          @current-change="loadLogs"
        />
      </div>
    </section>

    <el-dialog v-model="detail.visible" title="请求日志详情" width="760px">
      <el-descriptions v-if="detail.row" :column="2" border>
        <el-descriptions-item label="请求时间">{{ formatTime(detail.row.created_at) }}</el-descriptions-item>
        <el-descriptions-item label="状态码">
          <el-tag :type="detail.row.status_code >= 400 ? 'danger' : 'success'" effect="dark">
            {{ detail.row.status_code }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="本地 Key">{{ detail.row.local_key || '-' }}</el-descriptions-item>
        <el-descriptions-item label="API Key ID">{{ detail.row.api_key_id || '-' }}</el-descriptions-item>
        <el-descriptions-item label="命中渠道">{{ detail.row.channel_name || channelFallback(detail.row.channel_id) }}</el-descriptions-item>
        <el-descriptions-item label="渠道 ID">{{ detail.row.channel_id || '-' }}</el-descriptions-item>
        <el-descriptions-item label="请求模型"><span class="mono">{{ detail.row.model || '-' }}</span></el-descriptions-item>
        <el-descriptions-item label="上游模型"><span class="mono">{{ detail.row.upstream_model || '-' }}</span></el-descriptions-item>
        <el-descriptions-item label="请求路径"><span class="mono">{{ detail.row.path || '-' }}</span></el-descriptions-item>
        <el-descriptions-item label="耗时">{{ formatLatency(detail.row.latency_ms) }}</el-descriptions-item>
        <el-descriptions-item label="输入 Token">{{ detail.row.prompt_tokens || 0 }}</el-descriptions-item>
        <el-descriptions-item label="输出 Token">{{ detail.row.completion_tokens || 0 }}</el-descriptions-item>
        <el-descriptions-item label="总 Token">{{ detail.row.total_tokens || 0 }}</el-descriptions-item>
        <el-descriptions-item label="缓存 Token">{{ detail.row.cached_tokens || 0 }}</el-descriptions-item>
        <el-descriptions-item label="图片 Token">{{ detail.row.image_tokens || 0 }}</el-descriptions-item>
        <el-descriptions-item label="扣费">{{ detail.row.cost || 0 }}</el-descriptions-item>
        <el-descriptions-item label="输入倍率">{{ formatRatio(detail.row.prompt_ratio) }}</el-descriptions-item>
        <el-descriptions-item label="输出倍率">{{ formatRatio(detail.row.completion_ratio) }}</el-descriptions-item>
        <el-descriptions-item label="缓存倍率">{{ formatRatio(detail.row.cache_ratio) }}</el-descriptions-item>
        <el-descriptions-item label="图片倍率">{{ formatRatio(detail.row.image_ratio) }}</el-descriptions-item>
      </el-descriptions>
      <el-alert
        v-if="detail.row && detail.row.error_message"
        class="detail-error"
        :title="detail.row.error_message"
        type="error"
        show-icon
        :closable="false"
      />
    </el-dialog>
  </main>
</template>

<script>
import { ElMessage } from 'element-plus'
import { clearRequestLogs, deleteRequestLogs, fetchRequestLogs } from '@/api/logs'
import './log.css'

export default {
  name: 'LogView',
  data() {
    return {
      logs: [],
      selectedIds: [],
      page: 1,
      pageSize: 20,
      total: 0,
      filters: {
        start_date: '',
        end_date: '',
        channel: '',
        model: '',
        status: 'all'
      },
      loading: false,
      notice: '',
      detail: {
        visible: false,
        row: null
      }
    }
  },
  computed: {
    visibleIds() {
      return this.logs.map((item) => item.id)
    }
  },
  mounted() {
    this.loadLogs()
  },
  methods: {
    async loadLogs() {
      this.loading = true
      this.notice = ''
      try {
        const data = await fetchRequestLogs({
          page: this.page,
          page_size: this.pageSize,
          ...this.normalizedFilters()
        })
        this.logs = data.items || []
        this.total = Number(data.total || 0)
        this.selectedIds = this.selectedIds.filter((id) => this.visibleIds.includes(id))
      } catch (error) {
        this.notice = error.message
      } finally {
        this.loading = false
      }
    },
    searchLogs() {
      this.page = 1
      this.loadLogs()
    },
    normalizedFilters() {
      return Object.fromEntries(
        Object.entries(this.filters).filter(([, value]) => value && value !== 'all')
      )
    },
    resetFilters() {
      this.filters = {
        start_date: '',
        end_date: '',
        channel: '',
        model: '',
        status: 'all'
      }
      this.searchLogs()
    },
    handlePageSizeChange() {
      this.page = 1
      this.loadLogs()
    },
    async removeSelected() {
      if (!this.selectedIds.length) {
        return
      }
      const confirmed = window.confirm(`确定删除选中的 ${this.selectedIds.length} 条日志吗？`)
      if (!confirmed) {
        return
      }
      this.loading = true
      this.notice = ''
      try {
        await deleteRequestLogs(this.selectedIds)
        this.notice = '已删除选中日志'
        this.selectedIds = []
        await this.loadLogs()
      } catch (error) {
        this.notice = error.message
      } finally {
        this.loading = false
      }
    },
    async clearAll() {
      const confirmed = window.confirm('确定清空所有请求日志吗？这个操作不可恢复。')
      if (!confirmed) {
        return
      }
      this.loading = true
      this.notice = ''
      try {
        await clearRequestLogs()
        this.notice = '已清空请求日志'
        this.selectedIds = []
        this.page = 1
        await this.loadLogs()
      } catch (error) {
        this.notice = error.message
      } finally {
        this.loading = false
      }
    },
    async copyKey(row) {
      const key = row.local_key_full || row.local_key
      if (!key) return
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(key)
      } else {
        const textarea = document.createElement('textarea')
        textarea.value = key
        textarea.setAttribute('readonly', '')
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
      }
      ElMessage.success('Key 已复制')
    },
    formatTime(value) {
      if (!value) return '-'
      return new Date(value).toLocaleString()
    },
    formatRatio(value) {
      return Number(value || 0).toFixed(4)
    },
    formatLatency(value) {
      const ms = Number(value || 0)
      if (ms >= 1000) {
        return `${(ms / 1000).toFixed(2)} s`
      }
      return `${ms} ms`
    },
    channelFallback(id) {
      return id ? `#${id}` : '-'
    },
    openDetail(row) {
      this.detail.row = row
      this.detail.visible = true
    }
  }
}
</script>
